import { useAuth } from '@iamexperiences/feature-auth';
import { useQuery } from 'react-query';
import { useCallback, useMemo } from 'react';

import { settingsManager } from '../../shared/settings';

// --- Types ---
interface IDirectoryRole {
  roleTemplateId?: string
}
interface IGroupsSettings {
  userDelegationEnabled?: string
  selfServiceAppAccessEnabled?: string
  groupsInAccessPanelEnabled?: string
}
interface ServicePlan {
  servicePlanId: string
  servicePlanName: string
  provisioningStatus: string
  appliesTo: string
}
interface SubscribedSku {
  capabilityStatus: string
  consumedUnits: number
  servicePlans: ServicePlan[]
  skuId: string
  skuPartNumber: string
  appliesTo: string
}
interface SubscribedSkusResponse {
  value: SubscribedSku[]
}

// --- Constants ---
const GlobalAdminRoleTemplateId = '62e90394-69f5-4237-9190-012177145e10';
const UserAdministratorRoleTemplateId = 'fe930be7-5e62-47db-91af-98c3a49a38b1';
const GroupsAdministratorRoleTemplateId = 'fdd7a751-b60b-444a-984c-02652fe8fa1c';
const AAD_PREMIUM_SKU = 'AAD_PREMIUM';

// --- Utility Functions ---
const isAdmin = (directoryRoles?: IDirectoryRole[]) => {
  if (!directoryRoles) return false;
  return !!directoryRoles.find(
    (role) =>
      role.roleTemplateId === GlobalAdminRoleTemplateId ||
      role.roleTemplateId === UserAdministratorRoleTemplateId ||
      role.roleTemplateId === GroupsAdministratorRoleTemplateId
  );
};

const msGraphUri = (base: string) => ({
  directoryRoles: () => `${base}/v1.0/me/memberOf/microsoft.graph.directoryRole`,
  tenantLicense: () => `${base}/v1.0/subscribedSkus`
});

// consumer apps needs to set these. I don't think they all have those settings. Will need to either copy them over or set defaults.
const useGroupsConfig = () => {
  const { auth } = useAuth();

  // what MyApps is passing to <MyGroups>
  const getToken = useCallback(
    async (scope: string) => {
      return await auth.acquireToken({ scopes: [scope] });
    },
    [auth]
  );

  const configuration = useMemo(() => {
    return {
      msGraphBaseUrl: settingsManager.getRequired('msGraphResourceName'),
      clientId: settingsManager.getRequired('clientId'),
      myAccountBackendUrl: settingsManager.getRequired('myAccountResourceName'),
      myAccountClientId: settingsManager.getRequired('myAccountClientId'),
      getToken
    };
  }, [getToken]);

  const userData = {
    name: auth.user?.name || '',
    email: auth.user?.email || auth.user?.username,
    userId: auth.user?.objectId ?? '',
    tenantId: auth.user?.tenantId ?? '',
    userName: auth.user?.username ?? ''
  };

  return { configuration, userData };
};

// --- Data Fetching Hooks ---
export const useIsLicensePresent = () => {
  const { configuration } = useGroupsConfig();
  return useQuery({
    queryKey: ['license'],
    queryFn: async () => {
      const token = await configuration.getToken(configuration.msGraphBaseUrl);
      const url = msGraphUri(configuration.msGraphBaseUrl).tenantLicense();
      const response = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`${response.status}: Failed to fetch subscribedSkus.`);
      const skuResponse: SubscribedSkusResponse = await response.json();
      const skus: SubscribedSku[] = skuResponse.value;
      const premiumSku = skus.find((sku) =>
        sku.servicePlans.find((sp) => sp.servicePlanName.toLowerCase().includes(AAD_PREMIUM_SKU.toLowerCase()))
      );
      return !!premiumSku;
    },
    staleTime: Infinity
  });
};

const useDirectoryRoles = () => {
  const { configuration } = useGroupsConfig();
  return useQuery({
    queryKey: ['directoryRoles'],
    queryFn: async () => {
      const token = await configuration.getToken(configuration.msGraphBaseUrl);
      const url = msGraphUri(configuration.msGraphBaseUrl).directoryRoles();
      const response = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`${response.status}: Failed to fetch directory roles.`);
      const roles: IDirectoryRole[] = (await response.json()).value;
      return roles;
    },
    staleTime: Infinity
  });
};

const isApprovalManagementAvailable = (
  licenseData?: boolean,
  groupSettings?: IGroupsSettings,
  directoryRoles?: IDirectoryRole[]
) => {
  if (isAdmin(directoryRoles)) return true;
  return licenseData && (groupSettings?.userDelegationEnabled || groupSettings?.selfServiceAppAccessEnabled);
};

export const isJoinGroupAvailable = (licenseData?: boolean, groupSettings?: IGroupsSettings) =>
  licenseData && groupSettings?.userDelegationEnabled;

export const areGroupsEnabled = (groupSettings?: IGroupsSettings, directoryRoles?: IDirectoryRole[]) =>
  groupSettings?.groupsInAccessPanelEnabled || isAdmin(directoryRoles);

export const useGroupSettings = () => {
  const { configuration } = useGroupsConfig();
  return useQuery({
    queryKey: ['groupSettings'],
    queryFn: async () => {
      const token = await configuration.getToken(configuration.myAccountClientId);
      const url = `${configuration.myAccountBackendUrl}/api/groups/settings`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`${response.status}: Failed to fetch settings.`);
      return await (response.json() as Promise<IGroupsSettings>);
    },
    staleTime: Infinity
  });
};

export const useIsApprovalManagementAvailable = () => {
  const licenseQuery = useIsLicensePresent();
  const settingsQuery = useGroupSettings ();
  const directoryRolesQuery = useDirectoryRoles();
  return {
    ...licenseQuery,
    ...settingsQuery,
    isSuccess: licenseQuery.isSuccess && settingsQuery.isSuccess && directoryRolesQuery.isSuccess,
    data: isApprovalManagementAvailable(licenseQuery.data, settingsQuery.data, directoryRolesQuery.data),
    error: licenseQuery.error || settingsQuery.error || directoryRolesQuery.error
  };
};

export const useAreGroupsEnabled = () => {
  const settingsQuery = useGroupSettings();
  const directoryRolesQuery = useDirectoryRoles();
  return {
    ...settingsQuery,
    ...directoryRolesQuery,
    isSuccess: settingsQuery.isSuccess && directoryRolesQuery.isSuccess,
    data: areGroupsEnabled(settingsQuery.data, directoryRolesQuery.data),
    error: settingsQuery.error || directoryRolesQuery.error
  };
};
